import {
  AllCommunityModule,
  type ColDef,
  createGrid,
  type IDetailCellRendererParams,
  type GridApi,
  type ICellRendererParams,
  ModuleRegistry,
  type RowClickedEvent,
  themeQuartz,
  type ValueFormatterParams
} from "ag-grid-community";
import {
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  LicenseManager,
  MasterDetailModule,
  RowGroupingModule,
  SideBarModule,
  StatusBarModule
} from "ag-grid-enterprise";

import type { ProbeReport } from "@shared/probe_types";

import {
  execute_probe_session,
  submit_probe_report
} from "./browser_probe";
import {
  build_probe_report_view,
  compare_group_title,
  type ProbeObservationRow,
  type ProbeReportView,
  summarize_support_states,
  type SupportState
} from "./probe_report_view";
import "./styles.css";

interface ProbeUiState {
  readonly message: string;
  readonly message_tone: MessageTone;
  readonly report: ProbeReport | null;
}

type MessageTone = "error" | "info" | "success";

ModuleRegistry.registerModules([
  AllCommunityModule,
  SideBarModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  StatusBarModule,
  RowGroupingModule,
  MasterDetailModule
]);

const GRID_THEME = themeQuartz.withParams({
  backgroundColor: "#081120",
  foregroundColor: "#dbeafe",
  browserColorScheme: "dark",
  headerBackgroundColor: "#0f172a",
  headerTextColor: "#cbd5e1",
  cellTextColor: "#e2e8f0",
  accentColor: "#38bdf8",
  spacing: 10,
  wrapperBorder: {
    color: "rgba(96, 165, 250, 0.18)"
  },
  borderColor: "rgba(148, 163, 184, 0.18)",
  rowBorder: {
    color: "rgba(148, 163, 184, 0.12)",
    style: "solid"
  },
  oddRowBackgroundColor: "rgba(15, 23, 42, 0.68)"
});
const AUTO_GROUP_COLUMN_DEF: ColDef<ProbeObservationRow> = {
  headerName: "Probe",
  field: "title",
  minWidth: 320,
  flex: 1,
  cellRendererParams: {
    suppressCount: true
  }
};
const OBSERVATION_COLUMN_DEFS: readonly ColDef<ProbeObservationRow>[] = [
  {
    field: "group_title",
    headerName: "Group",
    rowGroup: true,
    sort: "asc",
    comparator: compare_group_title,
    hide: true
  },
  {
    field: "support_state",
    headerName: "Status",
    width: 176,
    filter: "agTextColumnFilter",
    comparator: compare_support_state,
    sort: "asc",
    cellRenderer: render_support_state_cell
  },
  {
    field: "duration_ms",
    headerName: "ms",
    width: 74,
    filter: "agNumberColumnFilter",
    valueFormatter: format_duration_value
  },
  {
    field: "implementation_kind",
    headerName: "Impl",
    width: 150,
    hide: true
  },
  {
    field: "requires_cross_origin_isolation",
    headerName: "Needs Isolation",
    width: 150,
    filter: "agTextColumnFilter",
    valueFormatter: format_boolean_value,
    hide: true
  },
  {
    field: "summary",
    headerName: "What It Checks",
    minWidth: 240,
    flex: 1,
    tooltipField: "summary",
    hide: true
  },
  {
    field: "detail_summary",
    headerName: "Observed Detail",
    minWidth: 320,
    flex: 1.2,
    tooltipField: "detail_json",
    hide: true
  },
  {
    field: "error_message",
    headerName: "Error",
    minWidth: 220,
    flex: 1,
    tooltipField: "error_message",
    valueFormatter: format_nullable_string,
    hide: true
  },
  {
    field: "probe_id",
    headerName: "Probe ID",
    minWidth: 220,
    hide: true
  },
  {
    field: "detail_json",
    headerName: "Detail JSON",
    minWidth: 360,
    flex: 1.5,
    hide: true
  }
] as const;
const app_root = get_app_root();
let grid_api: GridApi<ProbeObservationRow> | null = null;

configure_ag_grid_license();

/**
 * Browser-probe client entrypoint.
 */
async function main(): Promise<void> {
  const current_url = new URL(window.location.href);
  let report: ProbeReport | null = null;
  render_state({
    message: "Running full probe plan...",
    message_tone: "info",
    report
  });

  try {
    report = await execute_probe_session(current_url);
    render_state({
      message: "Submitting probe report...",
      message_tone: "info",
      report
    });

    await submit_probe_report(report);

    render_state({
      message: "Probe report submitted successfully.",
      message_tone: "success",
      report
    });
  } catch (error) {
    render_state({
      message: `Probe run failed: ${error_to_message(error)}`,
      message_tone: "error",
      report
    });
  }
}

function render_state(state: ProbeUiState): void {
  destroy_grid();
  const report_view =
    state.report === null ? null : build_probe_report_view(state.report);

  app_root.innerHTML = `
    <main class="shell">
      <section class="hero card">
        <div class="hero__copy">
          <p class="eyebrow">Browser Probe</p>
          <h1>Capability Report</h1>
          <p class="hero__lede">
            ${
              report_view === null
                ? "Running the probe now. Summary cards and the observation grid appear as soon as the page has a report to render."
                : escape_html(report_view.summary.headline)
            }
          </p>
        </div>
        <aside class="status_card status_card--${escape_html(state.message_tone)}">
          <span class="status_card__label">Run Status</span>
          <strong class="status_card__message">${escape_html(state.message)}</strong>
        </aside>
      </section>

      ${
        report_view === null
          ? build_loading_markup()
          : build_report_markup(report_view)
      }
    </main>
  `;

  if (report_view !== null) {
    render_observation_grid(report_view);
  }
}

function escape_html(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function error_to_message(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
}

function build_loading_markup(): string {
  return `
    <section class="card card--loading">
      <div class="section_heading">
        <h2>Waiting For Probe Results</h2>
        <p>The page will switch from a raw run state to a richer summary once the in-browser checks finish executing.</p>
      </div>
      <div class="summary_cards">
        ${build_card_markup("Profile", "Running", "neutral")}
        ${build_card_markup("Report", "Pending", "neutral")}
        ${build_card_markup("Observation Grid", "Stand By", "neutral")}
      </div>
    </section>
  `;
}

function build_report_markup(report_view: ProbeReportView): string {
  return `
    <section class="summary_cards">
      ${build_card_markup(
        "Report Status",
        format_report_status(report_view.summary.report_status),
        read_report_status_tone(report_view.summary.report_status)
      )}
      ${build_card_markup(
        "Supported",
        `${report_view.summary.supported_count}/${report_view.summary.total_observation_count}`,
        "positive"
      )}
      ${build_card_markup(
        "Unsupported",
        String(report_view.summary.unsupported_count),
        report_view.summary.unsupported_count > 0 ? "warning" : "neutral"
      )}
      ${build_card_markup(
        "Unknown",
        String(report_view.summary.unknown_count),
        report_view.summary.unknown_count > 0 ? "warning" : "neutral"
      )}
      ${build_card_markup(
        "Probe Errors",
        String(report_view.summary.error_count),
        report_view.summary.error_count > 0 ? "danger" : "neutral"
      )}
      ${build_card_markup(
        "Total Duration",
        format_duration_number(report_view.summary.total_duration_ms),
        "neutral",
        `${format_duration_number(report_view.summary.average_duration_ms)} avg`
      )}
    </section>

    <section class="card card--grid">
      <div class="grid_toolbar">
        <h2>Observations</h2>
        <span class="grid_toolbar__hint">Click a probe row to inspect its details.</span>
      </div>
      <div id="report-grid" class="report_grid"></div>
    </section>

    <details class="card raw_json">
      <summary>Raw JSON Report</summary>
      <pre class="report">${escape_html(report_view.raw_report_json)}</pre>
    </details>
  `;
}

function build_card_markup(
  label: string,
  value: string,
  tone: "danger" | "neutral" | "positive" | "warning",
  detail: string | null = null
): string {
  return `
    <article class="summary_card summary_card--${tone}">
      <span class="summary_card__label">${escape_html(label)}</span>
      <strong class="summary_card__value">${escape_html(value)}</strong>
      ${
        detail === null
          ? ""
          : `<span class="summary_card__detail">${escape_html(detail)}</span>`
      }
    </article>
  `;
}

function configure_ag_grid_license(): void {
  const license_key = import.meta.env.VITE_AG_GRID_LICENSE_KEY?.trim() ?? "";
  if (license_key.length > 0) {
    LicenseManager.setLicenseKey(license_key);
  }
}

function destroy_grid(): void {
  grid_api?.destroy();
  grid_api = null;
}

function render_observation_grid(report_view: ProbeReportView): void {
  const grid_root = document.getElementById("report-grid");
  if (!(grid_root instanceof HTMLElement)) {
    throw new Error("report grid mount point was not found");
  }

  grid_api = createGrid<ProbeObservationRow>(grid_root, {
    theme: GRID_THEME,
    autoGroupColumnDef: AUTO_GROUP_COLUMN_DEF,
    columnDefs: [...OBSERVATION_COLUMN_DEFS],
    defaultColDef: {
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: false,
      minWidth: 120,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
      suppressHeaderContextMenu: true
    },
    rowData: [...report_view.observation_rows],
    animateRows: true,
    domLayout: "autoHeight",
    masterDetail: true,
    detailRowAutoHeight: true,
    detailCellRenderer: ObservationDetailRenderer,
    isRowMaster: (row_data): boolean => row_data !== undefined,
    rowHeight: 30,
    headerHeight: 30,
    groupDisplayType: "singleColumn",
    groupDefaultExpanded: 1,
    onRowClicked: handle_row_click,
    sideBar: {
      toolPanels: ["columns", "filters"],
      defaultToolPanel: ""
    },
    tooltipShowDelay: 150,
    statusBar: {
      statusPanels: [{ statusPanel: "agTotalAndFilteredRowCountComponent" }]
    },
    getRowId: (params): string => params.data.probe_id
  });
}

function compare_support_state(
  left: SupportState,
  right: SupportState
): number {
  return read_support_priority(left) - read_support_priority(right);
}

function format_boolean_value(
  params: ValueFormatterParams<ProbeObservationRow, boolean | null | undefined>
): string {
  return params.value === true ? "Yes" : "No";
}

function format_duration_number(duration_ms: number): string {
  return `${duration_ms.toFixed(2)} ms`;
}

function format_duration_value(
  params: ValueFormatterParams<ProbeObservationRow, number | null | undefined>
): string {
  if (typeof params.value !== "number") {
    return "";
  }

  return params.value.toFixed(2);
}

function format_label(value: string): string {
  return value
    .split("_")
    .map((part): string => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function format_nullable_string(
  params: ValueFormatterParams<ProbeObservationRow, string | null | undefined>
): string {
  return params.value ?? "";
}

function format_report_status(status: ProbeReport["status"]): string {
  switch (status) {
    case "ok":
      return "OK";
    case "partial":
      return "Partial";
    case "error":
      return "Error";
  }
}

function read_report_status_tone(
  status: ProbeReport["status"]
): "danger" | "neutral" | "positive" {
  switch (status) {
    case "ok":
      return "positive";
    case "partial":
      return "neutral";
    case "error":
      return "danger";
  }
}

function render_support_state_cell(
  params: ICellRendererParams<ProbeObservationRow, SupportState | undefined>
): HTMLElement {
  if (params.node.group) {
    const support_states = (params.node.allLeafChildren ?? [])
      .map(
        (row_node): SupportState | null => row_node.data?.support_state ?? null
      )
      .filter((support_state): support_state is SupportState => {
        return support_state !== null;
      });
    const support_summary = summarize_support_states(support_states);
    const support_group_summary = document.createElement("span");
    const summary_label = document.createElement("span");
    const summary_ratio = document.createElement("span");

    support_group_summary.className = `support_group_summary support_group_summary--${support_summary.tone}`;
    summary_label.className = "support_group_summary__label";
    summary_label.textContent = support_summary.label;
    summary_ratio.className = "support_group_summary__ratio";
    summary_ratio.textContent = support_summary.ratio_label;

    support_group_summary.appendChild(summary_label);
    support_group_summary.appendChild(summary_ratio);

    return support_group_summary;
  }

  if (params.value == null) {
    return document.createElement("span");
  }

  const support_pill = document.createElement("span");
  const support_state = params.value;
  support_pill.className = `support_pill support_pill--${support_state}`;
  support_pill.textContent = format_support_label(support_state);
  support_pill.title = format_support_label(support_state);
  return support_pill;
}

function handle_row_click(
  event: RowClickedEvent<ProbeObservationRow>
): void {
  if (event.node.group) {
    event.node.setExpanded(!event.node.expanded);
    return;
  }

  if (event.data === undefined) {
    return;
  }

  collapse_other_detail_rows(event.node.id);
  event.node.setExpanded(!event.node.expanded);
}

function build_detail_chip_markup(label: string, value: string): string {
  return `
    <span class="detail_chip">
      <span class="detail_chip__label">${escape_html(label)}</span>
      <strong class="detail_chip__value">${escape_html(value)}</strong>
    </span>
  `;
}

function format_support_label(support_state: SupportState): string {
  switch (support_state) {
    case "supported":
      return "Supported";
    case "unsupported":
      return "Not Supported";
    case "unknown":
      return "Error";
  }
}

function read_support_priority(support_state: SupportState): number {
  switch (support_state) {
    case "unsupported":
      return 0;
    case "unknown":
      return 1;
    case "supported":
      return 2;
  }
}

function get_app_root(): HTMLElement {
  const root_element = document.getElementById("app");
  if (root_element === null) {
    throw new Error("probe root element was not found");
  }

  return root_element;
}

function collapse_other_detail_rows(active_node_id: string | undefined): void {
  if (grid_api === null) {
    return;
  }

  grid_api.forEachNode((row_node): void => {
    if (
      row_node.group ||
      row_node.id === undefined ||
      row_node.id === active_node_id ||
      !row_node.expanded
    ) {
      return;
    }

    row_node.setExpanded(false);
  });
}

function build_observation_detail_markup(
  observation_row: ProbeObservationRow
): string {
  return `
    <div class="observation_detail__header">
      <div>
        <p class="observation_detail__eyebrow">${escape_html(
          observation_row.group_title
        )}</p>
        <h3 class="observation_detail__title">${escape_html(
          observation_row.title
        )}</h3>
      </div>
      <div class="observation_detail__chips">
        ${build_detail_chip_markup(
          "Status",
          format_support_label(observation_row.support_state)
        )}
        ${build_detail_chip_markup(
          "Duration",
          format_duration_number(observation_row.duration_ms)
        )}
        ${build_detail_chip_markup(
          "Impl",
          format_label(observation_row.implementation_kind)
        )}
        ${build_detail_chip_markup(
          "Isolation",
          observation_row.requires_cross_origin_isolation ? "Required" : "No"
        )}
      </div>
    </div>
    <p class="observation_detail__summary">${escape_html(
      observation_row.summary
    )}</p>
    ${
      observation_row.error_message === null
        ? ""
        : `
          <div class="observation_detail__error">
            <strong>Error</strong>
            <span>${escape_html(observation_row.error_message)}</span>
          </div>
        `
    }
    <div class="observation_detail__block">
      <h4>Observed Detail</h4>
      <pre class="observation_detail__json">${escape_html(
        observation_row.detail_json
      )}</pre>
    </div>
  `;
}

class ObservationDetailRenderer {
  private element: HTMLDivElement;

  public constructor() {
    this.element = document.createElement("div");
  }

  public init(
    params: IDetailCellRendererParams<ProbeObservationRow, ProbeObservationRow>
  ): void {
    this.element.className = "observation_detail observation_detail--inline";
    if (params.data === undefined) {
      this.element.innerHTML = "";
      return;
    }
    this.element.innerHTML = build_observation_detail_markup(params.data);
  }

  public getGui(): HTMLDivElement {
    return this.element;
  }

  public refresh(
    params: IDetailCellRendererParams<ProbeObservationRow, ProbeObservationRow>
  ): boolean {
    if (params.data === undefined) {
      this.element.innerHTML = "";
      return true;
    }
    this.element.innerHTML = build_observation_detail_markup(params.data);
    return true;
  }
}

void main();
